import Phaser from 'phaser';
import { Palette, SceneKeys } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import type { PlayerProfile } from '@core/state/PlayerProfile';
import type { AudioService } from '@core/services/AudioService';

/** Opacidad de la capa oscura sobre el fondo para que el texto sea legible. */
const BACKGROUND_OVERLAY_ALPHA = 0.45;

/** Ancho por debajo del cual la UI se considera "angosta" y apila elementos. */
const NARROW_WIDTH_THRESHOLD = 760;

/** Clave bajo la cual el perfil del jugador vive en el registry global de Phaser. */
export const PLAYER_PROFILE_REGISTRY_KEY = 'playerProfile';

/** Clave bajo la cual el servicio de audio vive en el registry global de Phaser. */
export const AUDIO_REGISTRY_KEY = 'audioService';

/** Datos para renderizar la pantalla de resultados compartida. */
export interface RoundResultsOptions {
  readonly title: string;
  readonly score: number;
  readonly coins: number;
}

/**
 * Escena base de la que heredan todas las escenas del juego. Centraliza utilidades
 * comunes (acceso al perfil del jugador, audio, centro de pantalla, responsividad)
 * para evitar repetir codigo en cada escena.
 */
export abstract class BaseScene extends Phaser.Scene {
  protected get profile(): PlayerProfile {
    return this.registry.get(PLAYER_PROFILE_REGISTRY_KEY) as PlayerProfile;
  }

  protected get audio(): AudioService {
    return this.registry.get(AUDIO_REGISTRY_KEY) as AudioService;
  }

  protected get centerX(): number {
    return this.scale.width / 2;
  }

  protected get centerY(): number {
    return this.scale.height / 2;
  }

  /** Indica si el lienzo esta en orientacion vertical (mas alto que ancho). */
  protected get isPortrait(): boolean {
    return this.scale.height > this.scale.width;
  }

  /** Indica si el lienzo es angosto (celular vertical), util para apilar la UI. */
  protected get isNarrow(): boolean {
    return this.scale.width < NARROW_WIDTH_THRESHOLD;
  }

  /**
   * Vincula la escena al evento de resize del Scale Manager para reconstruir el
   * layout cuando la ventana cambia de tamaño u orientacion. Llamar al inicio de
   * create() en las escenas que deben reacomodarse.
   */
  protected initResponsive(): void {
    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    });
  }

  private handleResize(): void {
    if (this.scene.isActive()) {
      this.scene.restart();
    }
  }

  /**
   * Dibuja una imagen de fondo que cubre toda la pantalla (sin deformarse) con una
   * capa oscura encima para mantener la legibilidad. Centraliza este patron para que
   * todas las escenas usen el mismo fondo sin repetir codigo.
   */
  protected addBackground(texture: string): void {
    const { width, height } = this.scale;
    const background = this.add.image(this.centerX, this.centerY, texture);
    const coverScale = Math.max(width / background.width, height / background.height);
    background.setScale(coverScale);

    this.add.rectangle(this.centerX, this.centerY, width, height, Palette.background, BACKGROUND_OVERLAY_ALPHA);
  }

  /**
   * Pantalla de resultados compartida por los minijuegos (puntaje + estrellas y botones
   * para reintentar o volver). Acomoda los botones lado a lado o apilados segun el ancho.
   */
  protected showRoundResults(options: RoundResultsOptions): void {
    const { width, height } = this.scale;

    const overlay = this.add.rectangle(this.centerX, this.centerY, width, height, Palette.background, 0.85);
    overlay.setInteractive();

    this.add
      .text(this.centerX, height * 0.3, options.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '52px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, height * 0.45, `Puntos: ${options.score}\nEstrellas ganadas: ${options.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    const buttonsY = height * 0.66;
    if (this.isNarrow) {
      new TextButton(this, {
        x: this.centerX,
        y: buttonsY,
        label: 'Jugar de nuevo',
        color: Palette.secondary,
        onClick: () => this.scene.restart(),
      });
      new TextButton(this, {
        x: this.centerX,
        y: buttonsY + 100,
        label: 'Volver al menú',
        color: Palette.accent,
        onClick: () => this.scene.start(SceneKeys.Hub),
      });
      return;
    }

    new TextButton(this, {
      x: this.centerX - 180,
      y: buttonsY,
      label: 'Jugar de nuevo',
      color: Palette.secondary,
      onClick: () => this.scene.restart(),
    });
    new TextButton(this, {
      x: this.centerX + 180,
      y: buttonsY,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }
}
